import { useEffect, useState } from "react";
import { fetchData, fileUrl } from "../../APIs/useMyAPI";
import { Row, Col } from "../../Grid-system";
import { Title } from "../../components";
import Loading from "../../Tools/Loading";

// Jawaker reports its balance in "Tokens"; we divide by a configurable rate
// (tokens-per-USD) to show the USD value. The rate is editable and persisted.
const JAWAKER_ID = 1;
const JAWAKER_DIVISOR_KEY = "jawaker_divisor";
const DEFAULT_JAWAKER_DIVISOR = 100.11;

// Some providers (Mobitek) hold their wallet in LBP. We show the real LBP as the
// primary number but normalize it to USD for the color thresholds below.
const LBP_PER_USD = 89500;

// Color thresholds: < 1 => red, < 10 => yellow, otherwise green.
const balanceColors = (balance) => {
  if (balance === null || balance === undefined) {
    return { text: "text-gray-500", bg: "bg-gray-100", border: "border-gray-300" };
  }
  if (balance < 1) {
    return { text: "text-red-600", bg: "bg-red-50", border: "border-red-400" };
  }
  if (balance < 10) {
    return { text: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-400" };
  }
  return { text: "text-green-600", bg: "bg-green-50", border: "border-green-500" };
};

const formatBalance = (balance) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(balance);

const Balances = () => {
  const [balances, setBalances] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Kept as a string so the input stays editable while typing.
  const [jawakerDivisor, setJawakerDivisor] = useState(
    () => localStorage.getItem(JAWAKER_DIVISOR_KEY) || String(DEFAULT_JAWAKER_DIVISOR)
  );

  const divisorNum = parseFloat(jawakerDivisor);
  const effectiveDivisor =
    Number.isFinite(divisorNum) && divisorNum > 0 ? divisorNum : 1;

  const handleDivisorChange = (e) => {
    const val = e.target.value;
    setJawakerDivisor(val);
    const num = parseFloat(val);
    if (Number.isFinite(num) && num > 0) {
      localStorage.setItem(JAWAKER_DIVISOR_KEY, val);
    }
  };

  const fetchBalances = async () => {
    setIsLoading(true);
    try {
      const res = await fetchData(`admin/automated/balances`, {}, "GET");
      setBalances(res?.success?.balances || []);
    } catch (e) {
      setBalances([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Title title="API Balances" />
        <button
          onClick={fetchBalances}
          disabled={isLoading}
          className="rounded-2xl bg-Pink px-5 py-2 text-white transition-all hover:opacity-80 disabled:opacity-50"
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <Row className="mt-4">
          {balances.map((api) => {
            const isJawaker = api.id === JAWAKER_ID;
            const isLbp = api.currency === "LBP";
            const hasBalance = api.balance !== null && api.balance !== undefined;
            const displayBalance = hasBalance
              ? isJawaker
                ? api.balance / effectiveDivisor
                : api.balance
              : null;
            const displayCurrency = isJawaker ? "USD" : api.currency;
            // Value used only for color thresholds, normalized to USD.
            const colorValue = hasBalance
              ? isLbp
                ? api.balance / LBP_PER_USD
                : displayBalance
              : null;
            const colors = balanceColors(colorValue);
            return (
              <Col key={api.id} md={6} lg={4} className="mb-4">
                <div
                  className={`flex items-center gap-4 rounded-2xl border-2 ${colors.border} ${colors.bg} p-4 shadow-sm`}
                >
                  {api.logo ? (
                    <img
                      src={`${fileUrl}${api.logo}`}
                      alt={api.name}
                      className="h-12 w-12 shrink-0 rounded-full bg-white object-contain p-1"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-xl font-bold uppercase text-gray-500">
                      {api.name?.charAt(0)}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-lg font-semibold capitalize text-gray-800">
                      {api.name}
                    </div>
                    {hasBalance ? (
                      <>
                        <div className={`text-2xl font-bold ${colors.text}`}>
                          {formatBalance(displayBalance)}
                          {displayCurrency ? (
                            <span className="ml-1 text-sm font-medium">{displayCurrency}</span>
                          ) : null}
                        </div>
                        {isJawaker ? (
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                            <span>{formatBalance(api.balance)} Tokens ÷</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={jawakerDivisor}
                              onChange={handleDivisorChange}
                              className="w-20 rounded-lg border border-gray-300 px-2 py-0.5 text-xs focus:border-Pink focus:outline-none"
                            />
                          </div>
                        ) : null}
                        {api.balance_usd !== null &&
                        api.balance_usd !== undefined ? (
                          <div className="mt-1 text-xs text-gray-500">
                            {formatBalance(api.balance_usd)} USD
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="text-sm font-medium text-gray-500">
                        {api.error || "Unavailable"}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default Balances;
